import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import customerMapData from "../data/installation-data";
import customerCoordinateLookup from "../data/customer-coordinate-lookup";

const normalizeCoordinate = (value) => {
  const parsed = typeof value === "string" ? Number.parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

const parseLocationText = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    return null;
  }

  return {
    name: parts[0],
    city: parts[1] ?? "",
    state: parts.slice(2).join(", "),
    location: value,
  };
};

const normalizeLookupKey = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .toLowerCase()
    .replace(/[.'()]/g, "")
    .replace(/\s*&\s*/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

const lookupCoordinate = (...values) => {
  for (const value of values) {
    const key = normalizeLookupKey(value);

    if (!key) {
      continue;
    }

    const position = customerCoordinateLookup[key];

    if (position) {
      return position;
    }
  }

  return null;
};

const getLatLng = (customer) => {
  const lat = normalizeCoordinate(customer.lat ?? customer.latitude);
  const lng = normalizeCoordinate(customer.lng ?? customer.lon ?? customer.longitude);

  if (lat !== null && lng !== null) {
    return [lat, lng];
  }

  return lookupCoordinate(
    customer.name,
    customer.location,
    [customer.name, customer.city, customer.state].filter(Boolean).join(" "),
    [customer.name, customer.city].filter(Boolean).join(" ")
  );
};

const expandCustomerRecord = (customer) => {
  const locationFields = Object.keys(customer)
    .filter((key) => /^location\d+$/i.test(key))
    .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));

  if (locationFields.length) {
    return locationFields
      .map((field, index) => {
        const parsedLocation = parseLocationText(customer[field]);

        if (!parsedLocation) {
          return null;
        }

        return {
          id: `${customer.id}-${index}`,
          ...parsedLocation,
          position: null,
        };
      })
      .filter(Boolean);
  }

  const parsedLocation = parseLocationText(customer.location);

  return [
    {
      ...customer,
      name: customer.name ?? parsedLocation?.name ?? customer.location ?? "Customer",
      city: customer.city ?? parsedLocation?.city ?? "",
      state: customer.state ?? parsedLocation?.state ?? "",
      location: customer.location ?? parsedLocation?.location ?? "",
      position: getLatLng({
        ...customer,
        name: customer.name ?? parsedLocation?.name ?? "",
        city: customer.city ?? parsedLocation?.city ?? "",
        state: customer.state ?? parsedLocation?.state ?? "",
        location: customer.location ?? parsedLocation?.location ?? "",
      }),
    },
  ];
};

// Fix default marker icons broken by webpack 5 asset handling
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Fits the map to the bounds of all markers on first render
const FitBounds = ({ data }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!data.length) return;
    const bounds = L.latLngBounds(data.map((customer) => customer.position));
    map.fitBounds(bounds, { padding: [48, 48] });
  }, [map, data]);
  return null;
};

const CustomerMap = () => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customers = customerMapData.flatMap(expandCustomerRecord);
  const customersWithCoordinates = customers.filter((customer) => customer.position);

  return (
    <div className="page-container customer-map-page">
      <h1 className="main-heading">Customer Locations</h1>
      <p className="paragraph">
        Click on a marker to view customer details.
      </p>

      <div className="customer-map-layout">
        <div className="customer-map-container">
          <MapContainer
            center={[11.5, 78.5]}
            zoom={7}
            scrollWheelZoom={true}
            style={{ height: "100%", width: "100%" }}
          >
            <FitBounds data={customersWithCoordinates} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {customersWithCoordinates.map((customer) => (
              <Marker
                key={customer.id}
                position={customer.position}
                eventHandlers={{
                  click: () => setSelectedCustomer(customer),
                }}
              >
                <Popup>
                  <div className="customer-map-popup">
                    <strong className="popup-name">{customer.name}</strong>
                    <span className="popup-city">{customer.city}</span>
                    <span className="popup-state">{customer.state}</span>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <section className="customer-map-sidebar">
          <h2 className="customer-map-sidebar-title">Customers</h2>
          <div className="customer-chip-grid">
            {customers.map((customer) => (
              <button
                key={customer.id}
                className={`customer-chip${selectedCustomer?.id === customer.id ? " active" : ""}`}
                onClick={() => {
                  if (!customer.position) {
                    return;
                  }

                  setSelectedCustomer(selectedCustomer?.id === customer.id ? null : customer);
                }}
                title={customer.location || `${customer.name} — ${customer.city}`}
                disabled={!customer.position}
              >
                <span className="chip-name">{customer.name}</span>
                <span className="chip-city">{customer.city || customer.state || customer.location}</span>
              </button>
            ))}
          </div>

          {!customersWithCoordinates.length && (
            <div className="customer-map-detail-card">
              <p className="detail-card-row">
                <span className="detail-value">Map coordinates are currently unavailable for these customer records.</span>
              </p>
            </div>
          )}

          {selectedCustomer && (
            <div className="customer-map-detail-card">
              <h3 className="detail-card-name">{selectedCustomer.name}</h3>
              <p className="detail-card-row">
                <span className="detail-label">City</span>
                <span className="detail-value">{selectedCustomer.city}</span>
              </p>
              <p className="detail-card-row">
                <span className="detail-label">State</span>
                <span className="detail-value">{selectedCustomer.state}</span>
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CustomerMap;
